import React, { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, Grid, TextField, Tooltip, Typography, IconButton } from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { DataGrid, GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import { LoadingSpinner } from '@components';
import { generateUniqueKey, toCurrency } from '@helpers';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { Product, ProductCategory } from '@models';
import { useGetAllProductCategoriesQuery, useGetAllProductsQuery, useLazyGetSyncProductQuery, useVerifyUserMutation } from '@services';
import { setBrands, setSelectedProduct } from '@store/slices/product';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { setAllProducts } from '@store/slices/products';
import ProductCsvDownloadModal from '../ProductCsvDownloadModal/ProductCsvDownloadModal';
import ProductCsvUploadModal from '../ProductCsvUploadModal/ProductCsvUploadModal';
import ProductEditModal from '../ProductEditModal/ProductEditModal';
import { getZoneId } from '@helpers';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash.debounce';
import { CustomField } from '@models';
import { TwoFactorAuthenticationModal } from '@components';
import { showToast } from '@store/slices/notification';

type ProductsTableList = {
  id: number;
  sku: string;
  name: string;
  brand?: string;
  price?: number;
  categories: string[];
  zones: string[];
  custom_fields?: CustomField[];
}[];

const ProductList: FC = () => {
  const activeUser = useAppSelector((state) => state.activeUser.value);
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyUser] = useVerifyUserMutation();
  const [facode, setFacode] = useState('');
  const [isErrorCode, setIsErrorCode] = useState(false);

  const verify2FA = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault();

    try {
      setIsErrorCode(false);
      const value: any = await verifyUser({
        email: activeUser.user.email,
        code: facode,
      });

      if (value?.data?.code === 200) {
        setModalOpen(false);
        syncProduct();
        refetchProduct();
        setFacode('');

        dispatch(
          showToast({
            message: `Products synced to PWA successfully!`,
            type: 'success',
          }),
        );
      } else {
        setIsErrorCode(true);
      }
    } catch (err) {
      setIsErrorCode(true);
      console.log('===verify2FA error===', err);
    }
  };

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCsvDownloadModalOpen, setIsCsvDownloadModalOpen] = useState(false);
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false);
  const brands = useAppSelector((state) => state.product.value.brands);
  const { data: products, isLoading, refetch: refetchProduct } = useGetAllProductsQuery();
  const { data: categories } = useGetAllProductCategoriesQuery();
  const [syncProduct] = useLazyGetSyncProductQuery();

  const zoneId = getZoneId(categories);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'product');
  const hasWriteAccess = userAccessStatus.role > 1;
  const hasProductSyncAccess = userAccessStatus.role > 2;

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  // Subset of categories - only the subcategories of Menu1.
  const getCategories = (product: Product) => {
    return product.categories.reduce((productCategories, id) => {
      const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
      return category ? [...productCategories, category] : productCategories;
    }, [] as ProductCategory[]);
  };

  // Subset of categories - only the subcategories of Zones.
  const getZones = (product: Product) => {
    return product.categories.reduce((zones, id) => {
      const zone = categories?.find((c) => c.id === id && c.parent_id === zoneId);
      return zone ? [...zones, zone] : zones;
    }, [] as ProductCategory[]);
  };

  const dispatch = useAppDispatch();

  // If the store doesn't already have a brands array, generate one.
  useEffect(() => {
    if (products?.length && brands.length === 0) {
      const brandMap = new Map<string, string>();

      products?.forEach((product) => {
        const brand = product.custom_fields?.find((field) => field.name === 'brand');

        if (brand) {
          brandMap.set(brand.value, brand.value);
        }
      });

      dispatch(setBrands(Array.from(brandMap?.keys())));
    }

    if (products) {
      dispatch(setAllProducts(products));
    }
  }, [brands.length, dispatch, products]);

  const uniqueCategories = useMemo(() => {
    // subset of categories, it filters out Menu1, zones and subcategories of zones

    const filteredCategories = categories?.filter((category) => {
      return category.parent_id !== zoneId && category.id !== zoneId && category.id !== 40;
    });

    if (filteredCategories) {
      const categorySet: string[] = [];

      filteredCategories.forEach((category) => categorySet.push(category.name));

      return categorySet;
    }
  }, [categories, zoneId]);

  const openProductModal = (cell?: GridCellParams) => {
    if (hasWriteAccess) {
      let product: Product | undefined = {} as Product;

      if (cell) {
        product = products?.find((product) => product.id === cell.row.id);
      }

      if (!product) return;

      dispatch(setSelectedProduct(product));
      setIsProductModalOpen(true);
    }
  };

  const getCategoryNameById = (categoryId: number) => {
    const category = categories?.find((c) => {
      return c.id === categoryId;
    });

    if (category) {
      return category.name;
    } else {
      return 'No Category';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'sku',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'SKU'}
        </Typography>
      ),
      flex: 0.5,
    },
    {
      field: 'name',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Name'}
        </Typography>
      ),
      flex: 2.5,
      renderCell: (it) => {
        const { value, row } = it;
        const isBundleIndex = row.custom_fields.findIndex((field: { name: string; id: number }) => field.name === 'is_bundle');
        const isBundle = row?.custom_fields[isBundleIndex]?.value;
        return (
          <Tooltip title={value}>
            <Box display={'flex'} justifyContent={'center'}>
              {isBundleIndex > -1 && isBundle === 'true' && <DynamicFeedIcon sx={{ marginRight: 1 }} />}
              <span>{value}</span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'brand',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Brand'}
        </Typography>
      ),
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'categories',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Categories'}
        </Typography>
      ),
      flex: 1.5,
      renderCell: ({ value }) =>
        value?.map((category: string) => (
          <Tooltip title={value.join(', ')} key={generateUniqueKey()}>
            <span>
              <Chip label={category} size="small" sx={{ mr: 1 }} />
            </span>
          </Tooltip>
        )),
    },
    {
      field: 'zones',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Zones'}
        </Typography>
      ),
      flex: 1.5,
      renderCell: ({ value }) =>
        value?.map((category: string) => (
          <Tooltip title={value.join(', ')} key={generateUniqueKey()}>
            <span>
              <Chip label={category} size="small" sx={{ mr: 1 }} />
            </span>
          </Tooltip>
        )),
    },
    {
      field: 'price',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Price'}
        </Typography>
      ),
      flex: 0.5,
      valueFormatter: ({ value }) => toCurrency(Number(value)),
    },
  ];

  const rows = products?.reduce((products, product) => {
    const categories = getCategories(product).map(({ id }) => getCategoryNameById(id));

    if (!selectedCategories.length || selectedCategories.find((value) => categories.includes(value))) {
      products.push({
        id: product.id,
        sku: product.sku,
        name: product.name,
        brand: product.custom_fields?.find((field) => field.name === 'brand')?.value,
        categories,
        zones: getZones(product).map(({ id }) => getCategoryNameById(id)),
        price: product.price,
        custom_fields: product.custom_fields,
      });
    }

    return products;
  }, [] as ProductsTableList);

  const handleSearchTextChangeDebounced = useMemo(() => debounce((text: string) => setSearchText(text), 300), []);

  return isLoading ? (
    <LoadingSpinner isOpen={isLoading} />
  ) : (
    <React.Fragment>
      <Grid data-testid="product-list-grid" container spacing={2} sx={{ py: 2 }}>
        <Grid item xs={12}>
          <Box display="flex" marginTop={2}>
            <Button
              variant="contained"
              data-testid="download-product-button"
              sx={{ mr: 2 }}
              onClick={() => setIsCsvDownloadModalOpen(true)}
            >
              Download CSV
            </Button>
            {hasWriteAccess && (
              <React.Fragment>
                <Button
                  variant="contained"
                  data-testid="upload-product-button"
                  sx={{ mr: 2 }}
                  onClick={() => setIsCsvUploadModalOpen(true)}
                >
                  Upload CSV
                </Button>
                <Button variant="contained" data-testid="new-product-button" sx={{ mr: 2 }} onClick={() => openProductModal()}>
                  New product
                </Button>
              </React.Fragment>
            )}
            {hasProductSyncAccess && (
              <React.Fragment>
                <Tooltip title="Sync products to PWA">
                  <IconButton
                    color="primary"
                    data-testid="product-modal-button"
                    onClick={() => {
                      setModalOpen(true);
                      setFacode('');
                      setIsErrorCode(false);
                    }}
                  >
                    <CloudSyncIcon />
                  </IconButton>
                </Tooltip>
                <TwoFactorAuthenticationModal
                  isErrorCode={isErrorCode}
                  onSubmit={verify2FA}
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  fieldValue={facode}
                  onInputChange={(e) => setFacode(e.target.value)}
                  modalDescription={'Please enter the 2FA code below to proceed syncing products to PWA'}
                  errorDesc={'Invalid 2FA code. Please try again.'}
                />
              </React.Fragment>
            )}
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            options={(uniqueCategories && Array.from(uniqueCategories)) || []}
            getOptionLabel={(option: string) => option}
            renderInput={(params) => <TextField {...params} label="Filter by categories" />}
            multiple
            filterSelectedOptions
            onChange={(event, categories) => {
              setSelectedCategories(categories);
            }}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            placeholder="Search product"
            fullWidth
            onChange={(e) => handleSearchTextChangeDebounced(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
            }}
          />
        </Grid>
      </Grid>
      <Box sx={{ height: 800, width: '100%' }}>
        {!!rows && (
          <DataGrid
            rowHeight={50}
            rows={rows}
            columns={columns}
            headerHeight={50}
            disableSelectionOnClick
            onCellClick={openProductModal}
            filterModel={{
              items: [
                {
                  columnField: 'name',
                  operatorValue: 'contains',
                  value: searchText,
                },
              ],
            }}
            sx={{
              '&.MuiDataGrid-root': {
                border: 'none',
              },
              '.MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#CD9BF0',
                color: '#161616',
              },
              '& .MuiDataGrid-row': {
                '&:nth-child(2n)': { backgroundColor: 'rgba(235, 235, 235, .7)' },
                '&:hover': {
                  backgroundColor: '#faf2ff',
                },
              },
            }}
          />
        )}
      </Box>
      <ProductEditModal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} />
      {products && (
        <React.Fragment>
          <ProductCsvUploadModal isOpen={isCsvUploadModalOpen} onClose={() => setIsCsvUploadModalOpen(false)} products={products} />
          <ProductCsvDownloadModal isOpen={isCsvDownloadModalOpen} onClose={() => setIsCsvDownloadModalOpen(false)} products={products} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ProductList;
